import { useState, useEffect, useMemo, useRef } from "react";
import { useFormContext } from "react-hook-form";
import { format, isSameDay } from "date-fns";
import {
  DaysOfWeek,
  RecurringPeriodType,
} from "../../../../../../common/enums";
import { useAppContext } from "../../../../../../AppContext";
import { IEmployeeWithAvailabilityModel } from "../../../../../../common/components/forms/inputs/instructorCalendar/models/employeeCalendarModels";
import {
  IRecurringConflictItem,
  conflictDayNames,
} from "../models/recurringEventModels";
import { MAX_RECURRING_DURATION_IN_MINUTES } from "../../../../../../common/infrastructure/utilities/durationUtility";
import {
  getBookedBufferMinutes,
  getEffectiveBufferMinutes,
  toBlockedIntervals,
  toBusyInterval,
  toWorkingShifts,
} from "../../../../../../common/infrastructure/utilities/serviceBufferUtility";

export interface IUseRecurringEventSelectorProps {
  isRecurringFieldName: string;
  recurringDaysFieldName: string;
  recurringPeriodFieldName: string;
  recurringPeriodTypeFieldName: string;
  recurringIntervalFieldName: string;
  recurringIntervalTypeFieldName?: string;
  /** The event's name, when the custom availability check depends on it. */
  nameFieldName?: string;
  dateFieldName?: string;
  durationFieldName?: string;
  bufferAfterMinutes?: number;
  employeeIdFieldName?: string;
  studioEmployees?: IEmployeeWithAvailabilityModel[];
  /** Replaces the employee-bookings check; it may ignore `dates` and build its own. */
  onAvailabilityCheck?: (dates: Date[]) => Promise<IRecurringConflictItem[]>;
  onRecurringStatusChange?: (isRecurring: boolean, hasChecked: boolean, isFormValid: boolean, hasConflicts: boolean) => void;
  pageMode?: string;
}

export const useRecurringEventSelector = ({
  isRecurringFieldName,
  recurringDaysFieldName,
  recurringPeriodFieldName,
  recurringPeriodTypeFieldName,
  recurringIntervalFieldName,
  recurringIntervalTypeFieldName,
  nameFieldName,
  dateFieldName,
  durationFieldName,
  bufferAfterMinutes,
  employeeIdFieldName,
  studioEmployees,
  onAvailabilityCheck,
  onRecurringStatusChange,
  pageMode,
}: IUseRecurringEventSelectorProps) => {
  const { watch, setValue } = useFormContext();
  const { selectedStudio, isDesktopOrTablet } = useAppContext();

  // State
  const [isChecking, setIsChecking] = useState(false);
  const [conflicts, setConflicts] = useState<IRecurringConflictItem[]>([]);
  const [hasChecked, setHasChecked] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  // Bumped by every check and every reset, so an answer that arrives after the inputs changed
  // (or after a newer check started) is dropped instead of unlocking the submit.
  const checkRunRef = useRef(0);

  // Form values
  const isRecurring = watch(isRecurringFieldName);
  const rawSelectedDays = watch(recurringDaysFieldName);
  const selectedDays = useMemo(() => rawSelectedDays || [], [rawSelectedDays]);
  const recurringPeriod = watch(recurringPeriodFieldName) || 1;
  const recurringPeriodType =
    watch(recurringPeriodTypeFieldName) || RecurringPeriodType.Weeks;
  const recurringInterval = watch(recurringIntervalFieldName) || 1; // New: interval (every X weeks/months)
  const recurringIntervalType = recurringIntervalTypeFieldName 
    ? watch(recurringIntervalTypeFieldName) || RecurringPeriodType.Weeks
    : RecurringPeriodType.Weeks;
  const eventName = nameFieldName ? watch(nameFieldName) : null;
  const eventDate = dateFieldName ? watch(dateFieldName) : null;
  const eventDateTime = eventDate ? new Date(eventDate).getTime() : null;
  const duration = durationFieldName ? watch(durationFieldName) : null;
  const employeeId = employeeIdFieldName ? watch(employeeIdFieldName) : null;

  // Classes longer than 8 hours can't recur (occurrences would overlap), so the toggle is
  // disabled and force-cleared when the duration crosses that threshold.
  const isRecurringDisabled =
    Number(duration) > MAX_RECURRING_DURATION_IN_MINUTES;
  const recurringDisabledTooltip = isRecurringDisabled
    ? "Класове с продължителност над 8 часа не могат да бъдат повтарящи се."
    : undefined;

  useEffect(() => {
    if (isRecurringDisabled && isRecurring) {
      setValue(isRecurringFieldName, false);
    }
  }, [isRecurringDisabled, isRecurring, isRecurringFieldName, setValue]);

  // Weekdays configuration
  const weekdays = [
    { id: DaysOfWeek.Monday, label: "Пон", fullName: "Понеделник" },
    { id: DaysOfWeek.Tuesday, label: "Вт", fullName: "Вторник" },
    { id: DaysOfWeek.Wednesday, label: "Ср", fullName: "Сряда" },
    { id: DaysOfWeek.Thursday, label: "Чет", fullName: "Четвъртък" },
    { id: DaysOfWeek.Friday, label: "Пет", fullName: "Петък" },
    { id: DaysOfWeek.Saturday, label: "Съб", fullName: "Събота" },
    { id: DaysOfWeek.Sunday, label: "Нед", fullName: "Неделя" },
  ];

  // Validation functions
  const validateRecurringForm = (): string[] => {
    const errors: string[] = [];

    // Required field validations
    if (!selectedDays || selectedDays.length === 0) {
      errors.push("Моля, изберете поне един ден");
    }

    if (!recurringInterval || recurringInterval <= 0) {
      errors.push("Интервалът трябва да бъде поне 1");
    }

    if (!recurringPeriod || recurringPeriod <= 0) {
      errors.push("Периодът трябва да бъде поне 1");
    }

    // Numeric validations
    if (recurringInterval > 24) {
      errors.push("Интервалът не може да бъде повече от 24");
    }

    // Period max limit (3 months)
    if (
      Number(recurringPeriodType) === RecurringPeriodType.Months &&
      recurringPeriod > 3
    ) {
      errors.push("Периодът не може да бъде повече от 3 месеца");
    }

    if (
      Number(recurringPeriodType) === RecurringPeriodType.Weeks &&
      recurringPeriod > 12
    ) {
      errors.push("Периодът не може да бъде повече от 12 седмици");
    }

    // Period vs Interval logic validation
    const periodInWeeks =
      Number(recurringPeriodType) === RecurringPeriodType.Months
        ? recurringPeriod * 4
        : recurringPeriod;

    const intervalInWeeks =
      Number(recurringIntervalType) === RecurringPeriodType.Months
        ? recurringInterval * 4
        : recurringInterval;

    if (periodInWeeks < intervalInWeeks) {
      errors.push(
        "Периодът трябва да бъде поне толкова дълъг, колкото интервалът"
      );
    }

    // Maximum events prevention
    const estimatedEvents =
      Math.ceil(periodInWeeks / intervalInWeeks) * selectedDays.length;
    if (estimatedEvents > 100) {
      errors.push("Този график ще създаде твърде много събития (>100)");
    }

    return errors;
  };

  // Compute validation status
  const isFormValid = useMemo(() => {
    if (!isRecurring) return true;
    const errors = validateRecurringForm();
    setValidationErrors(errors);
    return errors.length === 0;
  }, [
    isRecurring,
    selectedDays,
    recurringInterval,
    recurringPeriod,
    recurringPeriodType,
    recurringIntervalType,
  ]);

  // Utility functions
  const toggleDay = (dayId: DaysOfWeek) => {
    const currentDays = selectedDays || [];
    const newDays = currentDays.includes(dayId)
      ? currentDays.filter((d: DaysOfWeek) => d !== dayId)
      : [...currentDays, dayId];

    setValue(recurringDaysFieldName, newDays);
    resetAvailabilityCheck();
  };

  const resetAvailabilityCheck = () => {
    checkRunRef.current += 1;
    setIsChecking(false);
    setHasChecked(false);
    setConflicts([]);
  };

  const getStartAndEndTimes = (): { startTime: string; endTime: string } => {
    if (!eventDate) return { startTime: "09:00", endTime: "10:00" };

    const startTime = format(eventDate, "HH:mm");

    // Calculate end time based on duration
    if (duration && typeof duration === "number") {
      const endDate = new Date(eventDate.getTime() + duration * 60000);
      const endTime = format(endDate, "HH:mm");
      return { startTime, endTime };
    }

    // Default to 1 hour if no duration
    const endDate = new Date(eventDate.getTime() + 60 * 60000);
    const endTime = format(endDate, "HH:mm");
    return { startTime, endTime };
  };

  const generateRecurringDates = (): Date[] => {
    if (!selectedDays || selectedDays.length === 0) return [];
    
    const startDate = eventDate || new Date();
    const interval = Math.max(1, recurringInterval || 1);
    
    // Keep the original time (e.g., 09:00)
    const startDay = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
    const timeOfDay = startDate.getTime() - startDay.getTime();

    // End boundary (inclusive by DATE)
    const endBoundary = Number(recurringPeriodType) === RecurringPeriodType.Months
      ? new Date(startDay.getFullYear(), startDay.getMonth() + recurringPeriod, startDay.getDate())
      : new Date(startDay.getFullYear(), startDay.getMonth(), startDay.getDate() + 7 * recurringPeriod);

    // Map domain enum to JavaScript day numbers (Sunday=0...Saturday=6)
    const dayMap = {
      [DaysOfWeek.Sunday]: 0,
      [DaysOfWeek.Monday]: 1,
      [DaysOfWeek.Tuesday]: 2,
      [DaysOfWeek.Wednesday]: 3,
      [DaysOfWeek.Thursday]: 4,
      [DaysOfWeek.Friday]: 5,
      [DaysOfWeek.Saturday]: 6,
    };

    const selectedDayNumbers = selectedDays
      .map((d: DaysOfWeek) => dayMap[d])
      .filter((d: number | undefined): d is number => d !== undefined);

    const results: Date[] = [];

    // Add the original start date
    results.push(new Date(startDate));

    // Generate recurring dates based on interval
    if (Number(recurringIntervalType) === RecurringPeriodType.Months) {
      let nextDate = new Date(startDay);
      nextDate.setMonth(nextDate.getMonth() + interval);
      
      while (nextDate <= endBoundary) {
        if (selectedDayNumbers.includes(nextDate.getDay())) {
          results.push(new Date(nextDate.getTime() + timeOfDay));
        }
        nextDate.setMonth(nextDate.getMonth() + interval);
      }
    } else {
      // For weekly intervals, start from week containing startDate + interval weeks
      for (let weekOffset = interval; ; weekOffset += interval) {
        const weekBaseDate = new Date(startDay);
        weekBaseDate.setDate(startDay.getDate() + weekOffset * 7);
        
        if (weekBaseDate > endBoundary) break;

        // Find the start of this week (Monday=0 system: Monday=0...Sunday=6)
        const daysSinceMonday = (weekBaseDate.getDay() + 6) % 7;
        const weekStart = new Date(weekBaseDate);
        weekStart.setDate(weekBaseDate.getDate() - daysSinceMonday);

        // For each selected day in this week
        for (const selectedDay of selectedDayNumbers) {
          const daysFromMonday = (selectedDay + 6) % 7;
          const candidate = new Date(weekStart);
          candidate.setDate(weekStart.getDate() + daysFromMonday);
          const candidateWithTime = new Date(candidate.getTime() + timeOfDay);
          
          if (candidateWithTime >= startDate && candidateWithTime <= endBoundary) {
            results.push(candidateWithTime);
          }
        }
      }
    }

    return results.sort((a, b) => a.getTime() - b.getTime());
  };

  const checkLocalAvailability = (dates: Date[]): IRecurringConflictItem[] => {
    if (!studioEmployees || !employeeId) {
      return [];
    }

    const selectedEmployee = studioEmployees.find(
      (emp) => emp.id === employeeId
    );
    if (!selectedEmployee) {
      return [];
    }

    const { startTime, endTime } = getStartAndEndTimes();
    const conflicts: IRecurringConflictItem[] = [];

    // Convert times to minutes for easier comparison
    const [startHour, startMin] = startTime.split(":").map(Number);
    const [endHour, endMin] = endTime.split(":").map(Number);
    const eventStartMinutes = startHour * 60 + startMin;
    const eventEndMinutes = endHour * 60 + endMin;

    dates.forEach((date) => {
      // The service's buffer keeps the employee busy after each occurrence, up to the end
      // of that day's working hours or the start of a blocked time that day. Booked slots
      // carry their own buffer in `busyUntil`.
      const occurrenceEndMinutes =
        eventEndMinutes +
        getEffectiveBufferMinutes(
          eventEndMinutes,
          bufferAfterMinutes,
          toWorkingShifts(
            selectedEmployee.workingDays.filter(
              (wd) => wd.dayOfWeek === date.getDay(),
            ),
          ),
          toBlockedIntervals(selectedEmployee.bookedSlots, date),
        );

      // Check for conflicts with booked slots
      selectedEmployee.bookedSlots.forEach((bookedSlot) => {
        const bookedStart = new Date(bookedSlot.startTime);
        const bookedEnd = new Date(bookedSlot.endTime);

        // Check if it's the same day
        if (isSameDay(date, bookedStart)) {
          const busy = toBusyInterval(bookedSlot, date);

          // Check for time overlap
          const hasOverlap =
            eventStartMinutes < busy.end && occurrenceEndMinutes > busy.start;

          if (hasOverlap) {
            conflicts.push({
              date: format(date, "dd.MM.yyyy"),
              dayName: conflictDayNames[date.getDay()],
              title: bookedSlot.serviceName || "Запазен час",
              startTime: format(bookedStart, "HH:mm"),
              endTime: format(bookedEnd, "HH:mm"),
              bufferAfterMinutes: getBookedBufferMinutes(bookedSlot),
              conflictType: "event",
            });
          }
        }
      });
    });

    return conflicts;
  };

  const checkAvailability = async () => {
    if (!selectedStudio?.id) return;

    const run = ++checkRunRef.current;
    setIsChecking(true);
    setConflicts([]);
    setHasChecked(false);

    try {
      const recurringDates = generateRecurringDates();

      if (onAvailabilityCheck) {
        // Use custom availability check if provided
        const foundConflicts = await onAvailabilityCheck(recurringDates);
        if (run !== checkRunRef.current) return;
        setConflicts(foundConflicts);
      } else if (studioEmployees && employeeId) {
        // Use local availability checking with employee data
        const foundConflicts = checkLocalAvailability(recurringDates);
        setConflicts(foundConflicts);
      } else {
        // No conflicts when no employee data or custom check is available
        setConflicts([]);
      }
      setHasChecked(true);
    } catch (error) {
      if (run !== checkRunRef.current) return;
      // A failed check proves nothing, so it doesn't count as checked: the submit stays
      // locked until a check succeeds.
      console.error("Error checking availability:", error);
      setConflicts([]);
    } finally {
      if (run === checkRunRef.current) setIsChecking(false);
    }
  };

  const getPreviewText = () => {
    if (!selectedDays || selectedDays.length === 0) return "";

    // Sort selected days by weekday order (Monday first)
    const sortedSelectedDays = weekdays
      .filter((day) => selectedDays.includes(day.id))
      .map((day) => day.label);

    const selectedDayNames = sortedSelectedDays.join(", ");

    const periodText =
      Number(recurringPeriodType) === RecurringPeriodType.Weeks
        ? `${recurringPeriod} седмиц${recurringPeriod > 1 ? "и" : "а"}`
        : `${recurringPeriod} месец${recurringPeriod > 1 ? "а" : ""}`;

    const intervalText =
      Number(recurringIntervalType) === RecurringPeriodType.Weeks
        ? recurringInterval === 1
          ? "всяка седмица"
          : `на всеки ${recurringInterval} седмиц${
              recurringInterval > 1 ? "и" : "а"
            }`
        : recurringInterval === 1
        ? "всеки месец"
        : `на всеки ${recurringInterval} месец${
            recurringInterval > 1 ? "а" : ""
          }`;

    return `${intervalText} в ${selectedDayNames} за ${periodText}`;
  };

  // Effects
  // Any input of the check makes its result stale: the series, the start, the length, the
  // employee (services, blocked time) and the name (the class wizard's same-name check).
  useEffect(() => {
    resetAvailabilityCheck();
  }, [
    selectedDays,
    recurringPeriod,
    recurringPeriodType,
    recurringInterval,
    recurringIntervalType,
    eventName,
    eventDateTime,
    duration,
    employeeId,
  ]);

  useEffect(() => {
    if (onRecurringStatusChange) {
      const finalHasChecked = pageMode === "edit" && isRecurring ? true : hasChecked;
      onRecurringStatusChange(isRecurring, finalHasChecked, isFormValid, conflicts.length > 0);
    }
  }, [isRecurring, hasChecked, isFormValid, conflicts, onRecurringStatusChange, pageMode]);

  return {
    // State
    isRecurring,
    isRecurringDisabled,
    recurringDisabledTooltip,
    selectedDays,
    recurringPeriod,
    recurringPeriodType,
    isChecking,
    conflicts,
    hasChecked,
    isDesktopOrTablet,

    // Validation
    validationErrors,
    isFormValid,

    // Data
    weekdays,

    // Actions
    toggleDay,
    checkAvailability,
    getPreviewText,
  };
};
